// Session Manager for caching API data
// This helps avoid repeated API calls and improves performance

import { CacheItem, CACHE_KEYS } from '@/models/services';

class SessionManager {
  private cache = new Map<string, CacheItem<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  // Set data in cache
  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  // Get data from cache
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    if (!item) return null;

    // Check if data is expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  // Check if data exists and is not expired
  has(key: string): boolean {
    const item = this.cache.get(key);
    if (!item) return false;

    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  // Remove specific item from cache
  delete(key: string): void {
    this.cache.delete(key);
  }

  // Clear all cache
  clear(): void {
    this.cache.clear();
  }

  // Get cache size
  size(): number {
    return this.cache.size;
  }

  // Clean expired items
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        this.cache.delete(key);
      }
    }
  }

  // Force refresh specific cache item
  refresh(key: string): void {
    this.cache.delete(key);
  }

  // Get cache keys
  keys(): string[] {
    return Array.from(this.cache.keys());
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

// Helper functions for common operations
export const cacheCustomers = (customers: any[]) => {
  sessionManager.set(CACHE_KEYS.CUSTOMERS, customers, 10 * 60 * 1000); // 10 minutes
};

export const getCachedCustomers = () => {
  return sessionManager.get(CACHE_KEYS.CUSTOMERS);
};

export const cacheProducts = (products: any[]) => {
  sessionManager.set(CACHE_KEYS.PRODUCTS, products, 10 * 60 * 1000); // 10 minutes
};

export const getCachedProducts = () => {
  return sessionManager.get(CACHE_KEYS.PRODUCTS);
};

export const cacheInvoices = (invoices: any[]) => {
  sessionManager.set(CACHE_KEYS.INVOICES, invoices, 5 * 60 * 1000); // 5 minutes
};

export const getCachedInvoices = () => {
  return sessionManager.get(CACHE_KEYS.INVOICES);
};

export const cacheNextInvoiceNumber = (number: string) => {
  sessionManager.set(CACHE_KEYS.NEXT_INVOICE_NUMBER, number, 1 * 60 * 1000); // 1 minute
};

export const getCachedNextInvoiceNumber = () => {
  return sessionManager.get(CACHE_KEYS.NEXT_INVOICE_NUMBER);
};

// Clear specific cache when data is updated
export const clearCustomersCache = () => {
  sessionManager.delete(CACHE_KEYS.CUSTOMERS);
};

export const clearProductsCache = () => {
  sessionManager.delete(CACHE_KEYS.PRODUCTS);
};

export const clearInvoicesCache = () => {
  sessionManager.delete(CACHE_KEYS.INVOICES);
  sessionManager.delete(CACHE_KEYS.NEXT_INVOICE_NUMBER);
};

// Clear all cache (useful for logout)
export const clearAllCache = () => {
  sessionManager.clear();
};

// Export the session manager instance
export default sessionManager; 